import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CloudArrowUp, X, Crosshair, ShieldCheck } from "@phosphor-icons/react";
import { api, http } from "@/lib/api";
import { useFieldCoverage, FieldCoverageBadge } from "@/components/FieldCoverage";
import { announceSlateApply } from "@/components/SlateBleedGuard";

// Sprint 63.3 — Field Model is bring-your-own-data. Each subscriber uploads
// exports from services THEY are licensed to use; files are private to the
// account, never pooled or redistributed. Only derived aggregates (median
// projection, max ownership, coverage) leave the backend.
const ACK_KEY = "wp_byod_ack_v1";
const DEFAULT_SLOTS = ["DFSARMY", "STOKASTIC", "SABERSIM"];
export const SOURCE_LABELS = { DFSARMY: "DFS Army", STOKASTIC: "Stokastic", SABERSIM: "SaberSim" };
const cleanLabel = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16);

export default function FieldModelPanel({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [checklist, setChecklist] = useState({ sources: {}, archetypes: [], archetype_labels: {} });
  const [howTo, setHowTo] = useState(false);
  const [ack, setAck] = useState(() => { try { return localStorage.getItem(ACK_KEY) === "1"; } catch { return false; } });
  const [bySlate, setBySlate] = useState([]);
  const loadBySlate = () => http.get("/competitors/by-slate").then((r) => setBySlate(r.data.slates || [])).catch(() => setBySlate([]));
  const loadChecklist = () => api.fieldChecklist().then(setChecklist).catch(() => {});
  const load = () => { loadBySlate(); loadChecklist(); return api.summary().then(setStatus).catch(() => setStatus(null)); };
  useEffect(() => {
    load();
    const on = () => load();
    window.addEventListener("wp:field-model-changed", on);
    return () => window.removeEventListener("wp:field-model-changed", on);
  }, []);
  const active = status?.competitor_sources || [];
  const { coverage, reload: reloadCoverage } = useFieldCoverage(active.length);
  const onChanged = () => { load(); reloadCoverage(); };
  const slots = [...active, ...DEFAULT_SLOTS.filter((d) => !active.includes(d))].slice(0, Math.max(3, active.length));
  const acknowledge = () => { try { localStorage.setItem(ACK_KEY, "1"); } catch { /* private mode */ } setAck(true); };
  // Sprint 63.9 — jump to a slate straight from the chip (swaps its sources in via per-slate memory).
  const [switching, setSwitching] = useState(null);
  const jumpToSlate = async (s) => {
    if (s.active || switching || s.resolvable === false) return;
    setSwitching(s.dg);
    try {
      const r = await api.dkSlateApply(s.dg);
      announceSlateApply(r);
      toast.success(`Switched to ${r.label || s.label} · ${r.player_count} players`);
      window.dispatchEvent(new CustomEvent("wp:slate-changed", { detail: { dg: s.dg } }));
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Slate switch failed"); } finally { setSwitching(null); }
  };
  return (
    <section className="bg-[#121212] border border-[#FFCC00]/40 p-4" data-testid="field-model-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="heading text-lg font-black uppercase inline-flex items-center gap-2">
            <Crosshair size={16} weight="fill" className="text-[#FFCC00]" /> Field Model · bring your own projections
          </div>
          {!compact && (
            <div className="text-[11px] text-zinc-400 mt-1 max-w-3xl leading-relaxed">
              Upload the projection + ownership exports from the sites you already subscribe to (CSV, or paste
              <span className="text-white"> Name, proj, own%</span> lines). Our projections stay ours. Their
              <span className="text-white"> median projection</span> becomes the shadow field the optimizer steps around, their
              <span className="text-white"> max ownership</span> drives the copies estimate, and where they
              <span className="text-white"> disagree</span> a player's range widens. Files are private to your account.
            </div>
          )}
        </div>
        <div className="mono text-[10px] uppercase tracking-widest text-right" data-testid="field-model-status">
          {active.length
            ? <span className="text-[#FFCC00] font-bold">ACTIVE · {active.length} source{active.length === 1 ? "" : "s"}</span>
            : <span className="text-zinc-500">inactive · building vs our internal field estimate</span>}
          <div className="mt-1 normal-case tracking-normal"><FieldCoverageBadge coverage={coverage} compact testId="field-model-coverage" /></div>
        </div>
      </div>
      {bySlate.length > 0 && (
        <div className="mb-3 border border-zinc-800 bg-black/30 px-3 py-2" data-testid="field-model-by-slate">
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Sources by slate · each slate keeps its own set, switching slates swaps them</div>
          <div className="flex flex-wrap gap-2">
            {bySlate.map((s) => (
              <button type="button" key={s.dg} data-testid={`field-model-slate-${s.dg}`} onClick={() => jumpToSlate(s)}
                disabled={s.active || switching !== null || s.resolvable === false} aria-pressed={s.active}
                className={`mono text-[10px] px-2 py-1 border text-left transition-colors ${s.active
                  ? "border-[#FFCC00]/60 text-[#FFCC00] cursor-default"
                  : s.resolvable === false
                    ? "border-zinc-900 text-zinc-600 cursor-not-allowed opacity-50"
                    : "border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14] disabled:opacity-60"}`}
                title={s.active ? `${s.sources.join(", ")} · up to ${s.n_players} players`
                  : s.resolvable === false ? `${s.label} is no longer on the DK lobby — its sources stay parked`
                  : `Switch to ${s.label} — its ${s.sources.length} source${s.sources.length === 1 ? "" : "s"} swap in, this slate's are parked`}>
                <span className={s.active ? "font-bold" : "text-zinc-300"}>{s.label}</span>
                <span className="text-zinc-500"> · {s.sources.length} source{s.sources.length === 1 ? "" : "s"} · {s.sources.join(" + ")}</span>
                {s.active ? <span className="ml-1 text-[#FFCC00]">● active</span>
                  : s.resolvable === false ? <span className="ml-1 text-zinc-600">· expired</span>
                  : <span className="ml-1">{switching === s.dg ? "switching…" : "→ jump"}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {(
        <div className="mb-3" data-testid="field-model-howto">
          <button type="button" onClick={() => setHowTo((v) => !v)} data-testid="field-model-howto-toggle"
            className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] hover:text-white border border-[#FFCC00]/40 hover:border-[#FFCC00] px-2 py-1">
            {howTo ? "Hide" : "What do I upload?"}
          </button>
          {howTo && (
            <div className="mt-2 border border-zinc-800 bg-black/40 p-3 text-[11px] text-zinc-300 leading-relaxed space-y-2">
              <div>
                Every site gets the same three steps in its own tile, in order. <span className="text-white">Step 1 is the only
                required one.</span> Each upload has its own button — you never drop two different files on the same button.
              </div>
              <div>
                <span className="text-[#FFCC00] font-bold">SaberSim</span> — step 1: <span className="text-white">Export Projections</span>
                {" "}(the full file, it has the sigma + percentile columns). Step 3: <span className="text-white">My Lineups</span> zip or CSV.
                Skip step 2, they don't publish contest exposures. No lineup export this week? Hit
                <span className="text-white"> Emulate their build</span> — graded at 4.3 of 6 bodies against their real portfolio.
              </div>
              <div>
                <span className="text-[#FFCC00] font-bold">DFS Army</span> — step 1: their <span className="text-white">player projections</span> export.
                Step 3: the <span className="text-white">lineups file</span> their optimizer built. If you don't build with them that week,
                Emulate reproduces <span className="text-white">82% of their lineups exactly</span> from step 1 alone.
              </div>
              <div>
                <span className="text-[#FFCC00] font-bold">Stokastic</span> — step 1: their <span className="text-white">Data Hub projections</span>.
                Step 2: one <span className="text-white">Contest Generator players</span> export per contest type (high stakes / marquee / low stakes) —
                these merge on top, they never replace step 1. Step 3: the <span className="text-white">Pre-Contest Lineups</span> file (post-simulation,
                the one you favourite from). Then hit Emulate on each archetype you loaded.
              </div>
              <div className="text-zinc-400">
                Real lineups always beat emulation, and anything emulated for a specific contest type is only faded in that type —
                Auto-Sense picks the archetype per tournament from its buy-in and field size. Nothing here is required to build;
                it only tightens how far your lineups stay from theirs.
              </div>
            </div>
          )}
        </div>
      )}
      {!ack ? (
        <div className="border border-zinc-800 bg-black/40 p-4" data-testid="byod-ack">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} weight="fill" className="text-[#39FF14] flex-shrink-0 mt-0.5" />
            <div className="text-[12px] text-zinc-300 leading-relaxed">
              <div className="text-white font-bold mb-1">Before you upload</div>
              WarPaint does not source, sell or share third-party projections. Anything you upload here must be data
              <span className="text-white"> you are licensed to use for your own play</span> (for example, an export from a
              service you pay for). Files are processed for your account only, never pooled with other users, and only
              derived aggregates (consensus median, max ownership, coverage %) are shown back to you.
              See <a href="/terms" className="text-[#39FF14] underline">Terms · User-Provided Data</a>.
            </div>
          </div>
          <button type="button" onClick={acknowledge} data-testid="byod-ack-btn"
            className="mt-3 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-black uppercase tracking-widest text-[10px] px-4 py-2">
            I understand — enable uploads
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {slots.map((s) => (
            <SourceZone key={s} initialLabel={s} active={active.includes(s)} onChanged={onChanged}
              info={checklist.sources?.[s]} archetypes={checklist.archetypes || []}
              archLabels={checklist.archetype_labels || {}} />
          ))}
        </div>
      )}
    </section>
  );
}

const ARCH_HELP = {
  high_stakes: "their $100+ contest generator",
  marquee: "their flagship big-field GPP generator",
  low_stakes: "their low buy-in generator",
};

function Step({ n, title, done, hint, children, testId }) {
  return (
    <div className="mt-2 pt-2 border-t border-zinc-900 first:border-0 first:mt-0 first:pt-0" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
          <span className={done ? "text-[#39FF14]" : "text-zinc-600"}>{done ? "✓" : n}</span> {title}
        </div>
        {hint ? <span className="mono text-[9px] text-zinc-600 text-right">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

function SourceZone({ initialLabel, active, onChanged, info, archetypes = [], archLabels = {} }) {
  const fileRef = useRef(null);
  const lineupRef = useRef(null);
  const archRef = useRef(null);
  const [label, setLabel] = useState(initialLabel);
  const [text, setText] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingArch, setPendingArch] = useState(null);
  const [shadow, setShadow] = useState(null);
  const site = cleanLabel(label) || initialLabel;
  const tid = initialLabel.toLowerCase();
  useEffect(() => {
    api.shadowLineupsList()
      .then((d) => setShadow((d.sources || []).find((s) => s.source === site) || null))
      .catch(() => setShadow(null));
  }, [site]);
  const projDone = (info?.players_with_projection || 0) > 0;
  const loadedArch = info?.archetype_exposures || {};
  const emulated = info?.emulated || {};
  const refreshShadow = async (key) => {
    const d = await api.shadowLineupsList().catch(() => ({ sources: [] }));
    setShadow((d.sources || []).find((s) => s.source === (key || site)) || null);
  };
  const uploadCsv = async (file, archetype = null) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.uploadCompetitor(site, file, archetype);
      if (r?.routed_to === "field_lineups") {
        toast.success(`${site}: ${r.lineups} field lineups loaded (${r.format})`, { description: r.note, duration: 9000 });
        await refreshShadow();
        onChanged?.();
        return;
      }
      const cols = r?.columns || {};
      if (r?.merged_into_existing) {
        toast.success(`${site}: ${archLabels[r.archetype] || "contest"} exposures merged in`, {
          description: `Their projections stay loaded · ${r.rows} players · exposures from "${cols.pool_exp_col || "Pool Exposure"}".`,
          duration: 8000,
        });
      } else {
        const ownNote = cols.own_col
          ? `ownership from "${cols.own_col}"${cols.own_mode === "cpt+flex" ? " (CPT + FLEX summed)" : ""} · ${r?.rows_with_own ?? 0} players`
          : "no ownership column found — ownership left blank";
        toast[cols.own_col ? "success" : "warning"](
          `${site}: ${r?.rows ?? ""} rows ingested · ${r?.coverage?.coverage_pct ?? "—"}% of playable pool covered`,
          { description: ownNote, duration: 7000 },
        );
      }
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || `${site} upload failed`, { duration: 12000 }); } finally { setBusy(false); }
  };
  const pasteManual = async () => {
    if (!text.trim()) { toast.error("Paste at least one line: Name, proj, own%"); return; }
    setBusy(true);
    try {
      const r = await http.post("/competitors/manual", { source: site, text });
      toast.success(`${site}: ${r.data.rows} players · ${r.data.matched_in_pool} matched in pool`);
      setText("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Manual entry failed"); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.deleteCompetitor(site); toast.info(`${site} removed`); onChanged?.(); } catch { toast.error("Remove failed"); }
  };
  const uploadLineups = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.shadowLineupsUpload(site, file);
      toast.success(`${site}: ${r.lineups} field lineups loaded (${r.format})`, {
        description: `Ranked by ${r.ranked_by || "projection"}${r.skipped_rows ? ` · ${r.skipped_rows} rows skipped (unmapped players)` : ""} — your builds now stay off these.`,
        duration: 8000,
      });
      await refreshShadow();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${site} lineup import failed`, { duration: 12000 });
    } finally { setBusy(false); }
  };
  const removeLineups = async () => {
    try { await api.shadowLineupsDelete(site); setShadow(null); onChanged?.(); toast.info(`${site} field lineups cleared`); } catch { toast.error("Clear failed"); }
  };
  const emulate = async (archetype = null) => {
    setBusy(true);
    try {
      const r = await api.shadowEmulate(site, 150, archetype, !!shadow);
      const how = { proj_max: "projection-max under their exposure + min-unique rails",
                    leverage: r.target_kind === "pool_exp" ? "matched to the exposures they deploy in that contest"
                      : r.exposure_target ? "matched to their published Optimal %" : "leverage-ranked sim optimals",
                    sim_optimal: `${r.sims} sims → ${r.unique_optimals} sim-optimal lineups` }[r.engine]
                 || "rebuilt from their own numbers";
      toast.success(`${site}: ${r.lineups} ${archLabels[r.archetype] ? `${archLabels[r.archetype].toLowerCase()} ` : ""}lineups rebuilt from their own numbers`, {
        description: `${how} · your builds now stay off them.`, duration: 9000,
      });
      await refreshShadow(r.source);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${site} emulation failed`, { duration: 14000 });
    } finally { setBusy(false); }
  };
  return (
    <div className={`border p-3 ${active ? "border-[#FFCC00]/60 bg-[#FFCC00]/5" : "border-zinc-800 bg-black/30"}`} data-testid={`field-model-${tid}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <input value={label} onChange={(e) => setLabel(cleanLabel(e.target.value))} disabled={active}
          data-testid={`field-model-label-${tid}`} title="Label this source (letters/numbers). Used only to tell your uploads apart."
          className="mono text-[10px] uppercase tracking-widest text-white font-bold bg-transparent border-b border-zinc-800 focus:border-[#FFCC00] outline-none w-28 disabled:opacity-100" />
        {active ? (
          <button type="button" onClick={remove} data-testid={`field-model-remove-${tid}`} className="text-zinc-500 hover:text-[#FF3B30]" title="Remove this source"><X size={12} /></button>
        ) : <span className="mono text-[9px] text-zinc-600">not loaded</span>}
      </div>

      <Step n="1" title="Their projections" done={projDone} testId={`field-model-step1-${tid}`}
        hint={projDone ? `${info.players_with_projection} players` : "required"}>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
          data-testid={`field-model-csv-${tid}`}
          title="Their player projections export (projections + ownership). Required — everything else builds on it."
          className="mt-1 w-full border border-dashed border-zinc-700 hover:border-[#FFCC00] text-zinc-400 hover:text-white mono text-[10px] uppercase tracking-widest py-2 inline-flex items-center justify-center gap-1.5">
          <CloudArrowUp size={12} /> {busy ? "Working…" : projDone ? "Replace projections" : "Upload projections CSV"}
        </button>
        <input ref={fileRef} type="file" accept=".csv,.zip,text/csv,application/zip" className="hidden" onChange={(e) => { uploadCsv(e.target.files?.[0]); e.target.value = ""; }} />
        {!showPaste ? (
          <button type="button" onClick={() => setShowPaste(true)} data-testid={`field-model-paste-open-${tid}`}
            className="mono text-[9px] text-zinc-600 hover:text-zinc-300 mt-1">no export? paste lines instead</button>
        ) : (
          <>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
              placeholder={"Jahmyr Gibbs, 24.1, 31\nJa'Marr Chase, 21.0, 27"}
              data-testid={`field-model-text-${tid}`}
              className="mt-1 w-full bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-1 mono text-[10px] placeholder:text-zinc-700" />
            <button type="button" onClick={pasteManual} disabled={busy}
              data-testid={`field-model-paste-${tid}`}
              className="mt-1 w-full bg-[#FFCC00] hover:bg-[#e0b400] disabled:opacity-50 text-black font-black uppercase tracking-widest text-[10px] py-1.5">
              Add lines
            </button>
          </>
        )}
      </Step>

      <Step n="2" title="Contest exposures" done={Object.keys(loadedArch).length > 0} testId={`field-model-step2-${tid}`}
        hint={Object.keys(loadedArch).length ? `${Object.keys(loadedArch).length} archetype${Object.keys(loadedArch).length === 1 ? "" : "s"}` : "SaberSim only"}>
        <div className="mono text-[9px] text-zinc-600 mt-0.5 leading-snug">
          SaberSim's contest-generator players export — the exposures they actually deploy per contest type.
          Merges on top of the projections. Skip this for sites that don't have a contest generator.
        </div>
        <div className="flex gap-1 mt-1">
          {archetypes.map((a) => (
            <button type="button" key={a} disabled={busy || !projDone}
              data-testid={`field-model-arch-${a}-${tid}`}
              onClick={() => { setPendingArch(a); archRef.current?.click(); }}
              title={!projDone ? "Upload their projections first" : `Upload ${ARCH_HELP[a] || a} players export`}
              className={`flex-1 border mono text-[9px] uppercase tracking-widest py-1.5 disabled:opacity-40 ${loadedArch[a]
                ? "border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10"
                : "border-zinc-800 text-zinc-500 hover:border-[#FFCC00] hover:text-white"}`}>
              {loadedArch[a] ? "✓ " : "+ "}{(archLabels[a] || a).replace(" stakes", "")}
            </button>
          ))}
        </div>
        <input ref={archRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={(e) => { uploadCsv(e.target.files?.[0], pendingArch); e.target.value = ""; setPendingArch(null); }} />
      </Step>

      <Step n="3" title="Their lineups" done={!!shadow || Object.keys(emulated).length > 0} testId={`field-model-step3-${tid}`}
        hint={shadow ? `${shadow.lineups} real` : Object.keys(emulated).length ? "emulated" : "best signal"}>
        <div className="flex gap-1 mt-1">
          <button type="button" onClick={() => lineupRef.current?.click()} disabled={busy}
            data-testid={`field-model-lineups-${tid}`}
            title="Their exported lineup set (SaberSim 'My Lineups' zip/CSV, DFS Army lineup grid, Stokastic pre-contest lineups). The real thing always beats emulation."
            className="flex-1 border border-dashed border-zinc-700 hover:border-[#39FF14] text-zinc-400 hover:text-[#39FF14] mono text-[10px] uppercase tracking-widest py-1.5 inline-flex items-center justify-center gap-1.5">
            <Crosshair size={11} /> {busy ? "Working…" : shadow ? "Replace lineups" : "Upload their lineups"}
          </button>
          {shadow && (
            <button type="button" onClick={removeLineups} data-testid={`field-model-lineups-clear-${tid}`}
              className="px-2 border border-zinc-800 text-zinc-500 hover:text-[#FF3B30] hover:border-[#FF3B30]" title="Clear these field lineups">
              <X size={11} />
            </button>
          )}
        </div>
        <input ref={lineupRef} type="file" accept=".csv,.zip,text/csv,application/zip" className="hidden"
          onChange={(e) => { uploadLineups(e.target.files?.[0]); e.target.value = ""; }} />
        {shadow && (
          <div className="mono text-[9px] text-zinc-600 mt-1" data-testid={`field-model-lineups-meta-${tid}`}>
            {shadow.format} · ranked by {shadow.ranked_by || "projection"}
            {shadow.skipped_rows ? ` · ${shadow.skipped_rows} skipped` : ""}
          </div>
        )}
        {projDone && (
          <div className="mt-1.5">
            <div className="mono text-[9px] text-zinc-600 mb-1">
              {shadow ? "or rebuild their portfolio per contest type:" : "no lineup export this week? rebuild their portfolio:"}
            </div>
            <div className="flex flex-wrap gap-1">
              <button type="button" onClick={() => emulate(null)} disabled={busy}
                data-testid={`field-model-emulate-${tid}`}
                title="Rebuild their portfolio from their own numbers, the way this site actually builds, and fade it."
                className="flex-1 border border-[#4DA6FF]/60 text-[#4DA6FF] hover:bg-[#4DA6FF] hover:text-black mono text-[9px] uppercase tracking-widest py-1.5 disabled:opacity-40">
                {busy ? "Working…" : emulated.any ? `✓ emulated · ${emulated.any.lineups}` : "Emulate their build"}
              </button>
              {archetypes.filter((a) => loadedArch[a]).map((a) => (
                <button type="button" key={a} onClick={() => emulate(a)} disabled={busy}
                  data-testid={`field-model-emulate-${a}-${tid}`}
                  title={`Rebuild the portfolio they deploy in ${(archLabels[a] || a).toLowerCase()} contests — auto-sense fades this one for those tournaments.`}
                  className={`flex-1 border mono text-[9px] uppercase tracking-widest py-1.5 disabled:opacity-40 ${emulated[a]
                    ? "border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10"
                    : "border-[#4DA6FF]/40 text-[#4DA6FF]/80 hover:bg-[#4DA6FF] hover:text-black"}`}>
                  {emulated[a] ? `✓ ${emulated[a].lineups}` : "Emulate"} {(archLabels[a] || a).replace(" stakes", "")}
                </button>
              ))}
            </div>
          </div>
        )}
      </Step>
    </div>
  );
}
